/**
 * Real OMEGA log API — calls gi-api-server endpoints.
 *
 * - GET /api/v1/omega/log/tail?since_seq=N&limit=M
 *   Returns a paginated slice of log entries with a next_seq cursor.
 *
 * - connectOmegaLogStream(opts) — wraps the browser EventSource API.
 *   Calls onEntries when a batch of new entries arrives.
 *   Calls onError on transport failure so the caller can fall back to
 *   polling /log/tail.
 */

import type { OmegaLogEntry, OmegaLogTailResponse } from '../types';
import { apiFetch, apiBaseUrl } from '../http';

export async function getOmegaLogTail(
  sinceSeq?: number,
  limit = 50,
  brainId?: string,
): Promise<OmegaLogTailResponse> {
  const params = new URLSearchParams();
  if (sinceSeq !== undefined) params.set('since_seq', String(sinceSeq));
  params.set('limit', String(Math.min(limit, 200)));
  // When set, the server filters the window to entries whose specialist is
  // bound to this brain (see omega_log::log_tail `brain_id`).
  if (brainId !== undefined) params.set('brain_id', brainId);
  const qs = params.toString();
  return apiFetch<OmegaLogTailResponse>(`/omega/log/tail${qs ? `?${qs}` : ''}`);
}

export interface StreamOptions {
  sinceSeq?: number;
  onEntries: (entries: OmegaLogEntry[]) => void;
  onError: (err: Event) => void;
}

/** Connect to the SSE stream. Returns a close() function. */
export function connectOmegaLogStream(opts: StreamOptions): () => void {
  const base = apiBaseUrl();
  const url = opts.sinceSeq !== undefined
    ? `${base}/omega/log/stream?since_seq=${opts.sinceSeq}`
    : `${base}/omega/log/stream`;

  const es = new EventSource(url);

  es.addEventListener('entries', (ev: MessageEvent) => {
    try {
      const entries = JSON.parse(ev.data as string) as OmegaLogEntry[];
      if (entries.length > 0) opts.onEntries(entries);
    } catch {
      // malformed JSON — ignore
    }
  });

  es.onerror = opts.onError;

  return () => es.close();
}
